import mustache from "mustache";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { CompraAnimalesResponse } from "../../../../../modulos/pesaje/ganaderia/response/CompraAnimalesResponse";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { daysFrom, formatFechaCorta } from "../../../../../utils/fechaUtils";
import { gdpRealKgDia } from "../../../../../utils/ganaderiaUtils";
import { _miles } from "../../../../../utils/numberUtils";
import template from "./lotesActivosTableTemplate.html";

mustache.parse(template);

/** Estados posibles del badge, en orden de prioridad para el ordenamiento
 *  ("urgencia de venta": GDP bajo primero, luego más cerca del óptimo de
 *  ocupación, sin pesaje al final). */
const ESTADO_PRIORIDAD = {
  "gdp-bajo": 0,
  pronto: 1,
  normal: 2,
  "en-curso": 3,
  "sin-pesaje": 4,
} as const;

type Estado = keyof typeof ESTADO_PRIORIDAD;

const ESTADO_LABEL: Record<Estado, string> = {
  "gdp-bajo": "GDP bajo",
  pronto: "Pronto",
  normal: "Normal",
  "en-curso": "En curso",
  "sin-pesaje": "Sin pesaje",
};

const ESTADO_BADGE_CLASS: Record<Estado, string> = {
  "gdp-bajo": "sx-badge-error",
  pronto: "sx-badge-warning",
  normal: "sx-badge-success",
  "en-curso": "sx-badge-neutral",
  "sin-pesaje": "sx-badge-neutral",
};

/** Tabla "Lotes activos" del dashboard de Pesaje/Ganadería: potreros con
 *  animales, tipos/razas presentes y GDP real (todo cruzado con las compras
 *  activas del lote, ya que ninguno de los dos vive en el lote) vs esperado
 *  (finca), y estado de ocupación vs el óptimo de la finca. El GDP real se
 *  calcula en cliente (`_computeGdpRealGr`) porque `ganaderiaGananciaDiariaTotalKg`
 *  del server no se recalcula con pesajes reales (queda pegado en el
 *  `gdpEsperadoGr` de la compra — confirmado con pruebas). Fecha de venta no
 *  existe a nivel de lote — queda pendiente de un campo de servidor. */
export class LotesActivosTableRender implements IConnectRender {
  /**
   * @param _lotes                Lotes de la finca (se filtran los activos).
   * @param _compras              Compras de la finca (para tipo/raza por lote).
   * @param _gdpEsperadoGr        `finca.ganaderiaCrecimientoDiarioGr`.
   * @param _diasOcupacionOptimo  `finca.ganaderiaDiasOcupacionOptimo`.
   */
  constructor(
    private readonly _lotes: ClienteFincaLoteResponse[],
    private readonly _compras: CompraAnimalesResponse[],
    private readonly _gdpEsperadoGr: number,
    private readonly _diasOcupacionOptimo: number,
  ) {}

  render(): string {
    const filas = this._lotes
      .filter((l) => l.ganaderiaCantidadAnimales > 0)
      .map((l) => this._buildFila(l))
      .sort((a, b) => a._orden - b._orden);

    return mustache.render(template, { lotes: filas });
  }

  connect(): void {}

  dispose(): void {}

  private _buildFila(lote: ClienteFincaLoteResponse) {
    const dias = lote.ganaderiaUltimoIngreso
      ? daysFrom(lote.ganaderiaUltimoIngreso)
      : null;

    // ganaderiaGananciaDiariaTotalKg del server no se recalcula con pesajes
    // reales (queda pegado en el gdpEsperadoGr de la compra) — lo calculamos
    // nosotros: (pesoReal − pesoEntradaPonderado) / días, usando las compras
    // activas del lote ponderadas por animalesDisponibles.
    const gdpRealGr = this._computeGdpRealGr(lote);
    const gdpRatio = gdpRealGr != null ? gdpRealGr / this._gdpEsperadoGr : null;
    const gdpColor =
      gdpRatio == null ? "" : gdpRatio >= 1 ? "green" : gdpRatio >= 0.85 ? "orange" : "red";

    const ocupacionRatio =
      dias != null && this._diasOcupacionOptimo > 0
        ? dias / this._diasOcupacionOptimo
        : null;

    let estado: Estado;
    if (gdpRealGr == null) {
      estado = "sin-pesaje";
    } else if (gdpColor === "red") {
      estado = "gdp-bajo";
    } else if (ocupacionRatio != null && ocupacionRatio >= 0.9) {
      estado = "pronto";
    } else if (ocupacionRatio != null && ocupacionRatio >= 0.6) {
      estado = "normal";
    } else {
      estado = "en-curso";
    }

    const ultimoPesajeDias = lote.ganaderiaFechaUltimoPesaje
      ? daysFrom(lote.ganaderiaFechaUltimoPesaje)
      : null;

    return {
      nombreLote: lote.nombreLote,
      tiposRazas: this._getTiposRazas(lote),
      animales: _miles(lote.ganaderiaCantidadAnimales),
      dias: dias != null ? _miles(dias) : "—",
      hasGdp: gdpRealGr != null,
      gdpValorTexto: gdpRealGr != null ? `${_miles(gdpRealGr)} g/día` : "Sin pesaje",
      gdpEsperadoTexto:
        gdpRealGr != null ? `/ esp. ${_miles(this._gdpEsperadoGr)}` : null,
      gdpColor,
      gdpBarPct: gdpRatio != null ? Math.min(Math.round(gdpRatio * 100), 100) : 0,
      ultimoPesajeFecha: lote.ganaderiaFechaUltimoPesaje
        ? formatFechaCorta(lote.ganaderiaFechaUltimoPesaje)
        : "Sin pesaje",
      ultimoPesajeHaceDias:
        ultimoPesajeDias != null ? this._formatHaceDias(ultimoPesajeDias) : null,
      estadoTexto: ESTADO_LABEL[estado],
      estadoBadgeClass: ESTADO_BADGE_CLASS[estado],
      _orden:
        ESTADO_PRIORIDAD[estado] * 1000 - (ocupacionRatio ?? 0) * 100,
    };
  }

  /** GDP real (g/día por animal), calculado en cliente porque el campo del
   *  server (`ganaderiaGananciaDiariaTotalKg`) no se recalcula con pesajes
   *  reales. `null` si no hay pesaje real, no hay compra activa que lo respalde, o el
   *  intervalo de días no es positivo (ver `gdpRealKgDia`). */
  private _computeGdpRealGr(lote: ClienteFincaLoteResponse): number | null {
    const gdpKg = gdpRealKgDia(lote, this._compras);
    return gdpKg != null ? Math.round(gdpKg * 1000) : null;
  }

  /** "hoy" / "hace 1 día" / "hace N días" a partir de `daysFrom` (inclusivo:
   *  hoy = 1, ayer = 2, ...). */
  private _formatHaceDias(dias: number): string {
    if (dias === 1) return "hoy";
    if (dias === 2) return "hace 1 día";
    return `hace ${_miles(dias - 1)} días`;
  }

  /** Tipos/razas de los animales actualmente en el lote, deduplicados —
   *  cruzando las compras activas (`animalesDisponibles > 0`) cuyo
   *  `loteActual` es este lote. `null` si no hay ninguna (dato inconsistente:
   *  el lote tiene animales pero ninguna compra activa los referencia). */
  private _getTiposRazas(lote: ClienteFincaLoteResponse): string | null {
    const combos = new Set<string>();
    for (const compra of this._compras) {
      if (compra.animalesDisponibles <= 0) continue;
      if (compra.loteActual.id !== lote.id) continue;
      combos.add(`${compra.tipoAnimal} (${compra.razaAnimal})`);
    }
    return combos.size > 0 ? [...combos].join(" · ") : null;
  }
}
